import type { ActivityEvent, Lead, Membership, Stage, Task } from "../../lib/types";
import type { FinanceInvoice, FinanceReceipt, RevenueGoal } from "../finance/contracts";
import { projectedCents, revenueCents, salesClosed } from "../finance/analytics";
import { civilDate, daysBetween, type Notice } from "../notifications/feed";
import { periodRange, shiftDay, type PeriodId } from "../leads/list";

/**
 * Fórmulas da página Início, separadas da apresentação (AGENTS §5, §10).
 *
 * Tudo aqui é puro e determinístico: recebe o estado e o instante, devolve
 * números com unidade, período e denominador declarados. Os textos dos
 * "insights" são regras publicadas — não há modelo de IA por trás deles, e a
 * interface não pode sugerir o contrário (§3, §27.4).
 */

const TIME_ZONE = "America/Sao_Paulo";

export interface DayRange {
  from: string;
  to: string;
}

/** Janela civil de uma ficha da Home; `todos` cai em 30 dias para não somar a base inteira. */
export function homeRange(period: PeriodId, now: string, custom?: { from: string | null; to: string | null }): DayRange {
  const range = periodRange(period, now, custom);
  if (range) return range;
  const today = civilDate(now, TIME_ZONE);
  return { from: shiftDay(today, -29), to: today };
}

export function rangeDays(range: DayRange): number {
  return Math.max(1, daysBetween(range.from, range.to) + 1);
}

/** Janela imediatamente anterior, com a mesma duração. */
export function previousRange(range: DayRange): DayRange {
  const days = rangeDays(range);
  const to = shiftDay(range.from, -1);
  return { from: shiftDay(to, 1 - days), to };
}

function inRange(day: string, range: DayRange): boolean {
  return day >= range.from && day <= range.to;
}

/** Série por dia da janela (um valor por dia civil, do mais antigo ao mais recente). */
export function dailySeries(range: DayRange, dates: ReadonlyArray<string>, weights?: ReadonlyArray<number>): number[] {
  const days = rangeDays(range);
  const series = Array.from({ length: days }, () => 0);
  dates.forEach((date, index) => {
    if (!inRange(date, range)) return;
    series[daysBetween(range.from, date)] += weights?.[index] ?? 1;
  });
  return series;
}

/** Rótulos do eixo X como no protótipo ("10/09"); em janelas longas, só alguns dias. */
export function dayLabels(range: DayRange): string[] {
  const days = rangeDays(range);
  return Array.from({ length: days }, (_, index) => {
    const day = shiftDay(range.from, index);
    return `${day.slice(8, 10)}/${day.slice(5, 7)}`;
  });
}

const percent = (value: number) =>
  Math.abs(value).toLocaleString("pt-BR", { maximumFractionDigits: 1 });

/**
 * Variação como o protótipo escreve: "▲ 219,7%" / "▼ 4,5%"; sem base anterior
 * a comparação é declarada em vez de inventada.
 */
export function deltaText(current: number, previous: number, suffix = "%"): string {
  if (!previous) return current ? "Sem base anterior" : "—";
  const change = ((current - previous) / previous) * 100;
  return `${current >= previous ? "▲" : "▼"} ${percent(change)}${suffix}`;
}

export type DeltaTone = "up" | "down" | "flat";

export function deltaTone(text: string): DeltaTone {
  return text.includes("▲") ? "up" : text.includes("▼") ? "down" : "flat";
}

export interface HomeKpis {
  revenueCents: number;
  leads: number;
  won: number;
  /** Taxa em %, ou nulo sem leads no período. */
  conversion: number | null;
  revenueDelta: string;
  leadsDelta: string;
  wonDelta: string;
  conversionDelta: string;
  conversionUp: boolean;
  series: { revenue: number[]; leads: number[]; won: number[]; conversion: number[] };
}

export interface HomeKpiInput {
  leads: readonly Lead[];
  receipts: readonly FinanceReceipt[];
  range: DayRange;
}

export function homeKpis({ leads, receipts, range }: HomeKpiInput): HomeKpis {
  const before = previousRange(range);
  const created = (window: DayRange) => leads.filter((lead) => inRange(civilDate(lead.createdAt, TIME_ZONE), window));
  const currentLeads = created(range);
  const previousLeads = created(before);
  const currentWon = salesClosed(receipts, range);
  const previousWon = salesClosed(receipts, before);
  const revenue = revenueCents(receipts, range);
  const previousRevenue = revenueCents(receipts, before);

  const rate = (won: number, total: number) => (total ? (100 * won) / total : null);
  const conversion = rate(currentWon.length, currentLeads.length);
  const previousConversion = rate(previousWon.length, previousLeads.length);

  const leadsSeries = dailySeries(range, currentLeads.map((lead) => civilDate(lead.createdAt, TIME_ZONE)));
  const wonSeries = dailySeries(range, currentWon.map((receipt) => receipt.receivedOn));
  const revenueSeries = dailySeries(
    range,
    receipts.map((receipt) => receipt.receivedOn),
    receipts.map((receipt) => receipt.amountCents),
  );
  let leadsSoFar = 0;
  let wonSoFar = 0;
  const conversionSeries = leadsSeries.map((count, index) => {
    leadsSoFar += count;
    wonSoFar += wonSeries[index];
    return leadsSoFar ? (100 * wonSoFar) / leadsSoFar : 0;
  });

  const revenueDelta = deltaText(revenue, previousRevenue);
  return {
    revenueCents: revenue,
    leads: currentLeads.length,
    won: currentWon.length,
    conversion,
    revenueDelta: revenueDelta + (previousRevenue ? " vs período anterior" : ""),
    leadsDelta: deltaText(currentLeads.length, previousLeads.length),
    wonDelta: deltaText(currentWon.length, previousWon.length),
    conversionDelta:
      conversion === null || previousConversion === null
        ? "—"
        : `${conversion >= previousConversion ? "▲" : "▼"} ${percent(conversion - previousConversion)} pp`,
    conversionUp: conversion !== null && previousConversion !== null && conversion >= previousConversion,
    series: { revenue: revenueSeries, leads: leadsSeries, won: wonSeries, conversion: conversionSeries },
  };
}

export function conversionText(conversion: number | null): string {
  return conversion === null ? "—" : `${conversion.toFixed(1).replace(".", ",")}%`;
}

// --- funil ------------------------------------------------------------------

export interface FunnelStageFlow {
  stage: Stage;
  /** Participações do período que chegaram a esta etapa (ou além). */
  reached: number;
  /** Fração do topo, 0–1. */
  ratio: number;
  /** Média de dias nas visitas já encerradas, ou nulo sem visita encerrada. */
  averageDays: number | null;
}

export interface FunnelFlowInput {
  funnelId: string;
  stages: readonly Stage[];
  memberships: readonly Membership[];
  leads: readonly Lead[];
  events: readonly ActivityEvent[];
  range: DayRange;
}

/**
 * Quanto do topo continua vivo em cada etapa, para as participações criadas no
 * período. Uma participação "chegou" a uma etapa quando está nela ou depois
 * dela, quando passou por ela (evento de etapa) ou quando foi ganha.
 */
export function funnelFlow({ funnelId, stages, memberships, leads, events, range }: FunnelFlowInput): FunnelStageFlow[] {
  const ordered = stages.filter((stage) => stage.funnelId === funnelId && !stage.archived).sort((a, b) => a.order - b.order);
  const index = new Map(ordered.map((stage, position) => [stage.id, position]));
  const leadById = new Map(leads.map((lead) => [lead.id, lead]));
  const rows = memberships.filter((membership) => {
    if (membership.funnelId !== funnelId) return false;
    const lead = leadById.get(membership.leadId);
    return Boolean(lead && !lead.archived && inRange(civilDate(lead.createdAt, TIME_ZONE), range));
  });

  const reachedBy = rows.map((membership) => {
    if (membership.status === "won") return ordered.length - 1;
    let furthest = index.get(membership.stageId) ?? 0;
    for (const event of events) {
      if (event.kind !== "stage" || event.membershipId !== membership.id || !event.stageId) continue;
      furthest = Math.max(furthest, index.get(event.stageId) ?? 0);
    }
    return furthest;
  });

  // Visitas encerradas: da entrada na etapa até a entrada na seguinte.
  const durations = new Map<string, number[]>();
  for (const membership of rows) {
    const entries = events
      .filter((event) => event.kind === "stage" && event.membershipId === membership.id && event.stageId)
      .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
    for (let position = 0; position < entries.length - 1; position += 1) {
      const stageId = entries[position].stageId!;
      const days = daysBetween(civilDate(entries[position].occurredAt, TIME_ZONE), civilDate(entries[position + 1].occurredAt, TIME_ZONE));
      durations.set(stageId, [...(durations.get(stageId) ?? []), Math.max(0, days)]);
    }
  }

  const top = reachedBy.length || 1;
  return ordered.map((stage, position) => {
    const reached = reachedBy.filter((furthest) => furthest >= position).length;
    const visits = durations.get(stage.id);
    return {
      stage,
      reached,
      ratio: reached / top,
      averageDays: visits?.length ? Math.round(visits.reduce((sum, days) => sum + days, 0) / visits.length) : null,
    };
  });
}

// --- insights (regras, não IA) ---------------------------------------------

export type InsightTone = "good" | "warn" | "acc";

export interface Insight {
  key: "previsao" | "risco" | "oportunidade";
  /** Trechos alternados: texto simples e destaque (`strong`). */
  parts: Array<{ text: string; strong?: boolean; numeric?: boolean }>;
  chip: string;
  tone: InsightTone;
}

export interface InsightInput {
  leads: readonly Lead[];
  memberships: readonly Membership[];
  stages: readonly Stage[];
  invoices: readonly FinanceInvoice[];
  receipts: readonly FinanceReceipt[];
  now: string;
  range: DayRange;
  /** Formata reais a partir de centavos. */
  money: (cents: number) => string;
}

export const RISK_QUIET_DAYS = 5;
export const CHANNEL_MIN_LEADS = 5;

export function homeInsights({ leads, memberships, stages, invoices, receipts, now, range, money }: InsightInput): Insight[] {
  const today = civilDate(now, TIME_ZONE);
  const projected = projectedCents(invoices, receipts, today, 30);

  const stageById = new Map(stages.map((stage) => [stage.id, stage]));
  const leadById = new Map(leads.map((lead) => [lead.id, lead]));
  const risky = memberships.filter((membership) => {
    if (membership.status !== "active") return false;
    const stage = stageById.get(membership.stageId);
    const lead = leadById.get(membership.leadId);
    if (!stage || !lead || lead.archived || !/proposta/i.test(stage.name)) return false;
    const reference = lead.lastInteractionAt ?? lead.createdAt;
    return daysBetween(civilDate(reference, TIME_ZONE), today) > RISK_QUIET_DAYS;
  });
  const riskValue = risky.reduce((sum, membership) => sum + (leadById.get(membership.leadId)?.potentialValue ?? 0), 0);

  const channels = new Map<string, { leads: number; won: number }>();
  for (const lead of leads) {
    if (lead.archived || !inRange(civilDate(lead.createdAt, TIME_ZONE), range)) continue;
    const channel = lead.source.trim() || "Sem origem";
    const current = channels.get(channel) ?? { leads: 0, won: 0 };
    current.leads += 1;
    if (lead.clientSince) current.won += 1;
    channels.set(channel, current);
  }
  const best = [...channels.entries()]
    .filter(([, stats]) => stats.leads >= CHANNEL_MIN_LEADS)
    .sort((a, b) => b[1].won / b[1].leads - a[1].won / a[1].leads)[0];

  return [
    {
      key: "previsao",
      parts: [
        { text: "Previsão: receita prevista de " },
        { text: money(projected), strong: true, numeric: true },
        { text: " nos próximos 30 dias, pelos contratos vigentes." },
      ],
      chip: `${money(projected)} previstos`,
      tone: "good",
    },
    risky.length
      ? {
          key: "risco",
          parts: [
            { text: "Risco: " },
            { text: `${risky.length} ${risky.length === 1 ? "proposta" : "propostas"}`, strong: true },
            { text: " somando " },
            { text: money(riskValue * 100), strong: true, numeric: true },
            { text: ` ${risky.length === 1 ? "está" : "estão"} há mais de ${RISK_QUIET_DAYS} dias sem contato.` },
          ],
          chip: "ação sugerida",
          tone: "warn",
        }
      : {
          key: "risco",
          parts: [{ text: `Risco: nenhuma proposta está há mais de ${RISK_QUIET_DAYS} dias sem contato.` }],
          chip: "acompanhamento em dia",
          tone: "warn",
        },
    best && best[1].won
      ? {
          key: "oportunidade",
          parts: [
            { text: "Oportunidade: " },
            { text: best[0], strong: true },
            { text: " tem a maior conversão entre os canais neste período: " },
            { text: `${((100 * best[1].won) / best[1].leads).toFixed(1).replace(".", ",")}%`, strong: true },
            { text: ". Priorize o acompanhamento dessa origem." },
          ],
          chip: "oportunidade sugerida",
          tone: "acc",
        }
      : {
          key: "oportunidade",
          parts: [{ text: "Oportunidade: acompanhe os canais com novos leads para identificar as próximas conversões." }],
          chip: "oportunidade sugerida",
          tone: "acc",
        },
  ];
}

// --- requer atenção ----------------------------------------------------------

export type AttentionTone = "crit" | "warn" | "info";

export interface AttentionItem {
  notice: Notice;
  tone: AttentionTone;
  /** Rótulo do botão, como no protótipo: Resolver / Retomar / Cobrar / Ver fluxo / Responder. */
  action: string;
}

export const ATTENTION_LIMIT = 4;

const SEVERITY_RANK = { critico: 0, atencao: 1, informativo: 2 } as const;

export function attentionItems(notices: readonly Notice[]): { items: AttentionItem[]; total: number } {
  const open = notices.filter((notice) => notice.actionable && !notice.resolvedAt);
  const sorted = [...open].sort(
    (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || b.occurredAt.localeCompare(a.occurredAt),
  );
  return {
    total: open.length,
    items: sorted.slice(0, ATTENTION_LIMIT).map((notice) => ({
      notice,
      tone: notice.severity === "critico" ? "crit" : notice.severity === "atencao" ? "warn" : "info",
      action:
        notice.category === "tarefa"
          ? "Resolver"
          : notice.category === "lead"
            ? "Retomar"
            : notice.category === "financeiro"
              ? "Cobrar"
              : notice.category === "automacao"
                ? "Ver fluxo"
                : "Responder",
    })),
  };
}

// --- meta do trimestre --------------------------------------------------------

export interface GoalOrbit {
  goal?: RevenueGoal;
  achievedCents: number;
  /** Fração ganha da meta, 0–1 (limitada a 1 no anel). */
  achievedRatio: number;
  /** Fração do trimestre já decorrida, 0–1. */
  elapsedRatio: number;
  remainingDays: number;
  expectedCents: number;
  label: string;
}

/** "T3 · 2026" para a data de hoje. */
export function quarterLabel(today: string): string {
  const month = Number(today.slice(5, 7));
  return `T${Math.floor((month - 1) / 3) + 1} · ${today.slice(0, 4)}`;
}

export function goalOrbit(goals: readonly RevenueGoal[], receipts: readonly FinanceReceipt[], now: string): GoalOrbit {
  const today = civilDate(now, TIME_ZONE);
  const goal = goals.find((item) => today >= item.periodStart && today <= item.periodEnd);
  if (!goal) {
    return { goal: undefined, achievedCents: 0, achievedRatio: 0, elapsedRatio: 0, remainingDays: 0, expectedCents: 0, label: quarterLabel(today) };
  }
  const achieved = revenueCents(receipts, { from: goal.periodStart, to: goal.periodEnd });
  const total = daysBetween(goal.periodStart, goal.periodEnd) + 1;
  const elapsed = Math.min(1, Math.max(0, (daysBetween(goal.periodStart, today) + 1) / total));
  return {
    goal,
    achievedCents: achieved,
    achievedRatio: goal.amountCents > 0 ? Math.min(1, achieved / goal.amountCents) : 0,
    elapsedRatio: elapsed,
    remainingDays: Math.max(0, daysBetween(today, goal.periodEnd)),
    expectedCents: Math.round(goal.amountCents * elapsed),
    label: quarterLabel(today),
  };
}

// --- tarefas de hoje ------------------------------------------------------------

export interface TodayTask {
  task: Task;
  hour: string;
  late: boolean;
  done: boolean;
}

const hourFormat = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: TIME_ZONE });

/** Tarefas com vencimento hoje; concluídas descem para o fim, como no protótipo. */
export function todayTasks(tasks: readonly Task[], now: string): TodayTask[] {
  const today = civilDate(now, TIME_ZONE);
  return tasks
    .filter((task) => civilDate(task.dueAt, TIME_ZONE) === today)
    .map((task) => ({
      task,
      hour: hourFormat.format(new Date(task.dueAt)),
      late: task.status !== "done" && task.dueAt < now,
      done: task.status === "done",
    }))
    .sort((a, b) => Number(a.done) - Number(b.done) || a.task.dueAt.localeCompare(b.task.dueAt));
}

const weekdayFormat = new Intl.DateTimeFormat("pt-BR", { weekday: "long", timeZone: TIME_ZONE });
const dayMonthFormat = new Intl.DateTimeFormat("pt-BR", { day: "numeric", month: "short", timeZone: TIME_ZONE });

/** "quarta, 16 de set" */
export function todayHint(now: string): string {
  const date = new Date(now);
  const weekday = weekdayFormat.format(date).split("-")[0];
  const day = dayMonthFormat.format(date).replace(".", "");
  return `${weekday}, ${day}`;
}

// --- desenho -----------------------------------------------------------------------

/** Curva suave do protótipo (`smoothPath`), com controle limitado ao intervalo dos pontos. */
export function smoothPath(points: ReadonlyArray<readonly [number, number]>): string {
  if (points.length === 0) return "";
  if (points.length < 3) return `M${points.map((point) => point.join(",")).join(" L")}`;
  const clamp = (value: number, a: number, b: number) => Math.min(Math.max(value, Math.min(a, b)), Math.max(a, b));
  let d = `M${points[0][0]},${points[0][1]}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const p0 = points[Math.max(0, index - 1)];
    const p1 = points[index];
    const p2 = points[index + 1];
    const p3 = points[Math.min(points.length - 1, index + 2)];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = clamp(p1[1] + (p2[1] - p0[1]) / 6, p1[1], p2[1]);
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = clamp(p2[1] - (p3[1] - p1[1]) / 6, p1[1], p2[1]);
    d += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2[0]},${p2[1]}`;
  }
  return d;
}

/** Curva da receita do protótipo (`zRevenuePath`): tangentes horizontais. */
export function revenuePath(points: ReadonlyArray<readonly [number, number]>): string {
  if (!points.length) return "";
  let path = `M${points[0].join(",")}`;
  for (let index = 1; index < points.length; index += 1) {
    const a = points[index - 1];
    const b = points[index];
    const dx = (b[0] - a[0]) / 3;
    path += ` C${a[0] + dx},${a[1]} ${b[0] - dx},${b[1]} ${b[0]},${b[1]}`;
  }
  return path;
}

/**
 * Relatório do período da Início, em CSV.
 *
 * O protótipo prometia "Relatório do período gerado — pronto para compartilhar"
 * num botão que só mostrava um aviso. A promessa vale; o que faltava era o
 * arquivo. Aqui ele sai com os mesmos números da tela — indicadores, funil,
 * carteira do período e os insights já escritos — para o relatório e a página
 * nunca discordarem (§10). Células que começam por `=`, `+`, `-` ou `@` saem
 * escapadas, senão a planilha as executa ao abrir (§26.3).
 */
export function homeReportCsv(input: {
  period: string;
  funnel: string;
  kpis: HomeKpis;
  flow: readonly FunnelStageFlow[];
  top: ReadonlyArray<{ name: string; company: string; stage: string; ltvCents: number }>;
  insights: readonly Insight[];
  money: (cents: number) => string;
}): string {
  const neutralize = (value: unknown) => {
    const text = String(value ?? "");
    return /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  };
  const cell = (value: unknown) => `"${neutralize(value).replaceAll('"', '""')}"`;
  const line = (values: unknown[]) => values.map(cell).join(";");
  const percent = (ratio: number | null) =>
    ratio === null ? "" : `${ratio.toFixed(1).replace(".", ",")}%`;

  const blocks = [
    [line(["Início", input.period, input.funnel])],
    [""],
    [line(["Indicador", "Valor", "Variação"])],
    [
      line(["Receita do período", input.money(input.kpis.revenueCents), input.kpis.revenueDelta]),
      line(["Leads captados", input.kpis.leads, input.kpis.leadsDelta]),
      line(["Vendas fechadas", input.kpis.won, input.kpis.wonDelta]),
      line(["Conversão", percent(input.kpis.conversion), input.kpis.conversionDelta]),
    ],
    [""],
    [line(["Etapa", "Participações", "Do topo", "Média de dias"])],
    input.flow.map((row) =>
      line([
        row.stage.name,
        row.reached,
        percent(row.ratio * 100),
        row.averageDays === null ? "" : row.averageDays,
      ]),
    ),
    [""],
    [line(["Lead", "Empresa", "Etapa", "Recebido"])],
    input.top.map((row) => line([row.name, row.company, row.stage, input.money(row.ltvCents)])),
    [""],
    [line(["Insight", "Leitura"])],
    input.insights.map((insight) =>
      line([insight.chip, insight.parts.map((part) => part.text).join("")]),
    ),
  ];

  return `\uFEFF${blocks.flat().join("\r\n")}`;
}
